import { Css, FormSectionLayout, Tag, useComputed, useTestIds } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo } from "react";
import { Price } from "src/components/Price";
import { ChangeEventWorkflow } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { changeEventQueryFor } from "src/routes/projects/change-events/guided/utils";
import { ProjectParams } from "src/routes/routesDef";
import { fail, formatCentsToPrice, queriesResult } from "src/utils";
import { ReviewImpactedPrice } from "../components/ReviewImpactedPrice";
import {
  ReviewConfigurationStep_ConfigChangeFragment,
  ReviewConfigurationStep_StructureFragment,
  useReviewConfigurationStepChangeEventQuery,
  useReviewConfigurationStepQuery,
} from "./ReviewConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "./applyConfigurationForm";
import { appliedById, ReviewConfigurationGroup, toReviewConfigurationSections } from "./reviewConfigurationUtils";

type ReviewConfigurationStepProps = { formState: ObjectState<ApplyConfigurationFormInput> };

/**
 * A completely read-only breakdown of the configuration the project would end up with, so the user can confirm it
 * before Review Impact.
 */
export function ReviewConfigurationStep(props: ReviewConfigurationStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  // Without a signed contract step 1 only dry runs the save, so the change event lives in the cache alone
  const { changeEventId, fetchPolicy } = useComputed(() => changeEventQueryFor(formState), [formState]);
  const structureQuery = useReviewConfigurationStepQuery({ variables: { structureId: projectId } });
  const changeEventQuery = useReviewConfigurationStepChangeEventQuery({
    variables: { structureId: projectId, changeEventId: changeEventId ?? fail("No change event ID") },
    fetchPolicy,
  });
  return queriesResult([structureQuery, changeEventQuery] as const, {
    data: ({ structure }, { changeEvent }) => (
      <ReviewConfigurationStepView
        formState={formState}
        structure={structure}
        configChange={changeEvent.configChange ?? fail("No config change")}
      />
    ),
  });
}

type ReviewConfigurationStepViewProps = ReviewConfigurationStepProps & {
  structure: ReviewConfigurationStep_StructureFragment;
  configChange: ReviewConfigurationStep_ConfigChangeFragment;
};

function ReviewConfigurationStepView(props: ReviewConfigurationStepViewProps) {
  const { formState, structure, configChange } = props;
  const { result, original, optionGroupChanges } = configChange;
  const isPartial = useComputed(
    () => formState.workflow.value === ChangeEventWorkflow.ApplyPartialConfiguration,
    [formState],
  );
  const optionIds = useComputed(() => formState.optionIds.value ?? [], [formState]);

  // Only partial lets the user pick which changes to apply, so only there is a row worth flagging as a diff
  const diffGroupIds = useMemo(() => {
    if (!isPartial) return new Set<string>();
    const applied = appliedById(optionGroupChanges, optionIds);
    return new Set(optionGroupChanges.filter((c) => applied.get(c.id)).map((c) => c.optionGroup.id));
  }, [isPartial, optionGroupChanges, optionIds]);
  const sections = useMemo(
    () => toReviewConfigurationSections(structure, result, diffGroupIds),
    [structure, result, diffGroupIds],
  );

  return (
    <FormSectionLayout
      title="Review Configuration"
      description="Review the resulting configuration and prices below."
      initialFields={
        isPartial && (
          <ConfigurationTotals
            newTotalInCents={result.config.reservedTotalPriceInCents}
            previousTotalInCents={original.config.reservedTotalPriceInCents}
          />
        )
      }
      withJumpLinks
      sections={sections.map((section, i) => ({
        title: section.title,
        fields: <ConfigReviewSection groups={section.groups} showColumnLabels={i === 0} />,
      }))}
    />
  );
}

type ConfigurationTotalsProps = { newTotalInCents: number; previousTotalInCents: number };

function ConfigurationTotals(props: ConfigurationTotalsProps) {
  const { newTotalInCents, previousTotalInCents } = props;
  const tid = useTestIds({}, "configurationTotals");
  return (
    <div css={Css.df.ba.bcGray200.br8.$} {...tid}>
      <div css={Css.df.fdc.aic.p2.fg1.br.bcGray200.$}>
        <span css={Css.xl.blue600.$} {...tid.newTotal}>
          <Price valueInCents={newTotalInCents} />
        </span>
        <span css={Css.sm.gray700.$}>New Configuration Total</span>
      </div>
      <div css={Css.df.fdc.aic.p2.fg1.$}>
        <span css={Css.xl.gray900.$} {...tid.delta}>
          <ReviewImpactedPrice valueInCents={newTotalInCents - previousTotalInCents} showZeroAsNone />
        </span>
        <span css={Css.sm.gray700.$}>Δ from Previous</span>
      </div>
    </div>
  );
}

type ConfigReviewSectionProps = { groups: ReviewConfigurationGroup[]; showColumnLabels: boolean };
function ConfigReviewSection(props: ConfigReviewSectionProps) {
  const { groups, showColumnLabels } = props;
  const tid = useTestIds({}, "configReviewGroup");
  return (
    <>
      {showColumnLabels && (
        <div css={Css.df.jcsb.sm.gray700.$} {...tid.columnLabels}>
          <span>Selection</span>
          <span>New Prices</span>
        </div>
      )}
      {groups.map((group) => (
        <div css={Css.df.fdc.gap1.pb2.bb.bcGray200.$} {...tid} key={group.title}>
          <div css={Css.df.aic.jcsb.gap2.$}>
            <div css={Css.df.aic.gap1.$}>
              <span css={Css.mdSb.gray900.$} {...tid.title}>
                {group.title}
              </span>
              {group.isDiff && <Tag type="warning" icon="error" text="Diff" {...tid.diffTag} />}
            </div>
            <span css={Css.mdSb.gray900.wsnw.$} {...tid.amount}>
              {formatGroupAmount(group.amountInCents)}
            </span>
          </div>
          {group.items.map((item) => (
            <div key={item.title} css={Css.df.aic.jcsb.gap2.$}>
              <span css={Css.sm.gray900.$} {...tid.itemTitle}>
                {item.title}
              </span>
              {item.amountInCents !== undefined && (
                <span css={Css.sm.gray900.wsnw.$} {...tid.itemAmount}>
                  {formatItemAmount(item.amountInCents)}
                </span>
              )}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

function formatGroupAmount(amountInCents: number): string {
  return amountInCents === 0 ? "$0" : formatCentsToPrice(amountInCents, { trim: true });
}

function formatItemAmount(amountInCents: number): string {
  // A selection that costs nothing reads better as "Included" than as "$0"
  return amountInCents === 0 ? "Included" : formatCentsToPrice(amountInCents, { trim: true });
}
